import React, { useState, useEffect } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { CHARITY_ROLE_PERMISSIONS } from '../../types/roles';
import type { CharityRole } from '../../types/roles';
import type { User } from '../../types/auth';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import DevStaffManagement from '@/components/dev/DevStaffManagement';
import { isDevEnvironment } from '@/utils/environment';

interface CharityUser extends Omit<User, 'role'> {
  role: CharityRole;
}

const CharityUsers: React.FC = () => {
  const { user } = useAuthV2();
  const role = (user?.role || 'volunteer') as CharityRole;
  const permissions = CHARITY_ROLE_PERMISSIONS[role];
  const isDevelopment = isDevEnvironment();

  const [members, setMembers] = useState<CharityUser[]>([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState<Partial<CharityUser>>({
    name: '',
    email: '',
    role: 'volunteer',
  });

  useEffect(() => {
    // Fetch charity members
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      // API call to fetch charity members
      // const response = await api.getCharityMembers();
      // setMembers(response.data);
      // For now, using mock data
      const mockMembers: CharityUser[] = [
        {
          id: '1',
          name: 'John Doe',
          email: 'john@example.com',
          role: 'owner',
          profilePicture: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: '2',
          name: 'Jane Smith',
          email: 'jane@example.com',
          role: 'volunteer',
          profilePicture: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ];
      setMembers(mockMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // API call to add new user
      // await api.addCharityMember(newUser);
      setIsAddingUser(false);
      setNewUser({ name: '', email: '', role: 'volunteer' });
      fetchMembers();
    } catch (error) {
      // Handle error
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: CharityRole) => {
    try {
      // API call to update member role
      // await api.updateCharityMemberRole(memberId, newRole);
      console.log(`Updating member ${memberId} role to ${newRole}`);
      fetchMembers();
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      try {
        // API call to remove user
        // await api.removeCharityMember(userId);
        console.log(`Removing user ${userId}`);
        fetchMembers();
      } catch (error) {
        console.error('Error removing member:', error);
      }
    }
  };

  const navigate = useNavigate();

  // In development mode, always allow access to staff management
  if (!permissions.canManageStaff && !isDevelopment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="space-y-6">
            <div className="flex items-center">
              <h1 className="text-xl font-medium text-navy-blue">Charity Users</h1>
            </div>
            <p>You don't have permission to manage charity members.</p>
          </div>
        </div>
      </div>
    );
  }

  // In development mode, use the DevStaffManagement component for a consistent experience
  if (isDevelopment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="space-y-6">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => navigate('/charity/dashboard')}
                className="mr-4 flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
            </div>
            <DevStaffManagement orgType="charity" />
          </div>
        </div>
      </div>
    );
  }
  
  // Regular production mode with the original implementation
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/charity/dashboard')}
              className="mr-4 text-gray-500 hover:text-navy-blue"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Charity Members</h1>
          </div>
          <div className="flex justify-end">
        <button
          onClick={() => setIsAddingUser(true)}
          className="bg-sky-blue text-white px-4 py-2 rounded-md hover:bg-sky-blue-dark"
        >
          Add Member
        </button>
      </div>

      {/* Add User Form */}
      {isAddingUser && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4">Add New Member</h2>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={newUser.role}
                onChange={(e) => {
                  setNewUser((prev) => ({
                    ...prev,
                    role: e.target.value as CharityRole,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
              >
                <option value="volunteer">Volunteer</option>
                <option value="staff">Staff</option>
                <option value="supervisor">Supervisor</option>
                {(role === 'owner' || role === 'manager') && <option value="manager">Manager</option>}
                {role === 'owner' && <option value="owner">Owner</option>}
              </select>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setIsAddingUser(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-sky-blue text-white px-4 py-2 rounded-md hover:bg-sky-blue-dark"
              >
                Add User
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {members.map((member) => (
              <tr key={member.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {member.profilePicture && (
                      <img
                        className="h-10 w-10 rounded-full"
                        src={member.profilePicture}
                        alt={member.name}
                      />
                    )}
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{member.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{member.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={member.role}
                    onChange={(e) => handleUpdateRole(member.id, e.target.value as CharityRole)}
                    className="text-sm text-gray-900 border-gray-300 rounded-md focus:border-sky-blue focus:ring-sky-blue"
                    disabled={
                      // Only owners can modify other owners
                      (member.role === 'owner' && role !== 'owner') ||
                      // Only owners and managers can modify managers
                      (member.role === 'manager' && role !== 'owner' && role !== 'manager')
                    }
                  >
                    <option value="volunteer">Volunteer</option>
                    <option value="staff">Staff</option>
                    <option value="supervisor">Supervisor</option>
                    {(role === 'owner' || role === 'manager') && <option value="manager">Manager</option>}
                    {role === 'owner' && <option value="owner">Owner</option>}
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleRemoveUser(member.id)}
                    className="text-red-600 hover:text-red-900"
                    disabled={
                      // Only owners can remove owners
                      (member.role === 'owner' && role !== 'owner') ||
                      // Only owners and managers can remove managers
                      (member.role === 'manager' && role !== 'owner' && role !== 'manager')
                    }
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
    </div>
  );
};

export default CharityUsers; 